import {
    ChronoUnit,
    convert,
    DateTimeFormatter,
    Duration,
    Instant,
    LocalDateTime,
    nativeJs,
    ZonedDateTime,
    ZoneId,
} from "@js-joda/core";
import "@js-joda/timezone";
import {ColumnSortEvent} from "primereact/column";
import {T} from "services/lang/LanguageService";
import {errorToaster} from "components/pages/measurement-devices/utils/utils";
import {DATE_FORMATTER_MINUTES, DATE_PATTERN_MINUTES} from "./constants";

export const FALLBACK_TIMEZONE = ZoneId.systemDefault();
export const LT_TIMEZONE = ZoneId.of("Europe/Vilnius");
export const UTC_TIMEZONE = ZoneId.of("UTC");

export function durationSeconds(duration: number, durationUnit: string): number {
  switch (durationUnit) {
    case "SECONDS":
      return duration;
    case "MINUTES":
      return duration * 60;
    case "HOURS":
      return duration * 60 * 60;
    case "DAYS":
      return duration * 60 * 60 * 24;
    case "WEEKS":
      return duration * 60 * 60 * 24 * 7;
    case "MONTHS":
      return duration * 60 * 60 * 24 * 7 * 30;
    default:
      throw new Error(T("error.not-known-time-unit"));
  }
}

export const dateTimeNow = (pattern: string) => formatDateTimeOrNow(pattern);

export function formatDateTimeOrNow(pattern: string, date?: Date | String | null) {
  if (date != null) {
    return formatDateTime(date, pattern);
  }

  return formatDateTime(new Date(), pattern);
}

export function formatDateTime(
  date: Date | String | null | undefined,
  pattern: string,
  defaultNone: string = ""
) {
  const tempDate = castToLocalDateTime(date);
  if (!tempDate) return defaultNone;

  return tempDate.format(DateTimeFormatter.ofPattern(pattern));
}

export function castToLocalDateTime(date: Date | String | null | undefined): LocalDateTime {
  if (date != null) {
    if (date instanceof Date) {
      return LocalDateTime.from(nativeJs(date));
    }
    return LocalDateTime.from(nativeJs(new Date(date as string)));
  }

  return LocalDateTime.from(nativeJs(new Date()));
}

export function formatDuration(secs: number) {
  let fmt = "";

  const days = Math.floor(secs / (3600 * 24));
  const hours = Math.floor((secs - days * 3600 * 24) / 3600);
  const minutes = Math.floor((secs - days * 3600 * 24 - hours * 3600) / 60);
  const seconds = secs - days * 3600 * 24 - hours * 3600 - minutes * 60;

  if (days > 0) {
    fmt += `${days}d `;
  }
  if (hours > 0) {
    fmt += `${hours}h `;
  }
  if (minutes > 0) {
    fmt += `${minutes}m `;
  }
  if (seconds >= 0) {
    fmt += `${seconds.toFixed(0)}s`;
  }

  return fmt;
}

export function getDiffSeconds(date1: Date | string, date2: Date | string): number {
  const d1 = new Date(toDate(date1)!);
  const d2 = new Date(toDate(date2)!);

  return (+d1 - +d2) / 1000;
}

export const toInstant = (value: Instant | ZonedDateTime | Date | string | number): Instant => {
  if (value instanceof Instant) return value;

  if (typeof value === "string") {
    return new Date(value).toInstant()
  }

  if (value instanceof Date) {
    return value.toInstant();
  }

  if(value instanceof ZonedDateTime) {
    return value.toInstant();
  }

  if(typeof value === "number") {
    return Instant.ofEpochMilli(value);
  }

  throw new Error(`${T("error.invalid-instant-type")} ${value} (${typeof value})`);
};

export function getMonday(
  date: Date | string | Instant | ZonedDateTime,
  zoneId: ZoneId,
): Date {
  const zonedDate = toZonedDateTime(date, zoneId);

  const day = zonedDate.dayOfWeek().value();
  const diff = day === 0 ? -6 : 1 - day; // adjust when day is monday

  return convert(zonedDate.plusDays(diff)).toDate()
}

function getBeginningOfMonth(date: Date, zoneId: ZoneId) {
  const zonedTime = toZonedDateTime(date, zoneId);
  return convert(zonedTime.withDayOfMonth(1).truncatedTo(ChronoUnit.DAYS)).toDate();
}

function getBeginningOfYear(date: Date, zoneId: ZoneId) {
  const zonedTime = toZonedDateTime(date, zoneId);
  return convert(zonedTime.withMonth(1).withDayOfMonth(1).truncatedTo(ChronoUnit.DAYS)).toDate();
}

export const dateToReadableString = (date: Date | undefined, formatUntil?: ChronoUnitLegacy) => {
  let returnee = "";
  const tempDate = date || new Date();

  returnee += tempDate.getFullYear();
  if (formatUntil === ChronoUnitLegacy.YEARS) {
    return returnee;
  }

  returnee += `-${tempDate.getMonth() + 1 >= 10 ? tempDate.getMonth() + 1 : `0${tempDate.getMonth() + 1}`}`;
  if (formatUntil === ChronoUnitLegacy.MONTHS) {
    return returnee;
  }

  returnee += `-${tempDate.getDate() >= 10 ? tempDate.getDate() : `0${tempDate.getDate()}`}`;
  if (formatUntil === ChronoUnitLegacy.DAYS) {
    return returnee;
  }

  returnee += ` ${tempDate.getHours() >= 10 ? tempDate.getHours() : `0${tempDate.getHours()}`}`;
  if (formatUntil === ChronoUnitLegacy.HOURS) {
    return returnee;
  }

  returnee += `:${tempDate.getMinutes() >= 10 ? tempDate.getMinutes() : `0${tempDate.getMinutes()}`}`;
  if (formatUntil === ChronoUnitLegacy.MINUTES) {
    return returnee;
  }

  return `${returnee}:${tempDate.getSeconds() >= 10 ? tempDate.getSeconds() : `0${tempDate.getSeconds()}`}`;
};

export function fmtDisplayDate(
  date: Instant | ZonedDateTime | Date | string | number | undefined,
  conf: {
    displayZoneId: ZoneId;
    companyZoneId?: ZoneId | undefined;
    pattern?: string | DateTimeFormatter | undefined;
    showOffset?: boolean;
  }): String {
  return formatDisplayDatetime(
    date,
    conf.displayZoneId,
    conf.pattern,
    conf.showOffset || false,
    conf.companyZoneId,
  );
}

export function formatDisplayDatetime(
  date: Instant | ZonedDateTime | Date | string | number | undefined,
  displayZoneId: ZoneId,
  pattern: string | DateTimeFormatter | undefined = undefined,
  showOffset: boolean = false,
  companyZoneId?: ZoneId | undefined,
) {
  if (!date) return "";

  if(pattern instanceof DateTimeFormatter){
    pattern = pattern || DATE_FORMATTER_MINUTES
  } else {
    pattern = pattern ? DateTimeFormatter.ofPattern(pattern) : DATE_FORMATTER_MINUTES;
  }

  const instant = toInstant(date);

  const localDateTime = instant.atZone(displayZoneId).toLocalDateTime();
  let dateStr = localDateTime.format(pattern);
  if(showOffset) {
    const offset = instant.atZone(displayZoneId).offset().toString();
    const timeDiff = companyZoneId ? Duration.between(instant.atZone(companyZoneId).toLocalDateTime(), localDateTime).toHours() : 0;
    dateStr += `[${offset}][diff:${timeDiff}]`;
  }

  return dateStr;
}

export enum ChronoUnitLegacy {
  SECONDS = "SECONDS",
  MINUTES = "MINUTES",
  HALFHOUR = "HALFHOUR",
  HOURS = "HOURS",
  DAYS = "DAYS",
  WEEKS = "WEEKS",
  MONTHS = "MONTHS",
  YEARS = "YEARS",
}

export type DweenTimeUnit = keyof typeof ChronoUnitLegacy;
export type DweenTimeUnitStr = keyof typeof ChronoUnitLegacy | string;

export function truncateDate(
  date: Date | Instant | ZonedDateTime | string,
  truncateTo: ChronoUnitLegacy | DweenTimeUnitStr | undefined | null,
  zoneId: ZoneId
): Date {
  if(!zoneId) throw new Error("ZoneId is required");

  date = toDate(date)!;

  if (truncateTo == null) {
    return date;
  }

  date.setMilliseconds(0);
  if (truncateTo === ChronoUnitLegacy.SECONDS) return date;

  date.setSeconds(0);
  if (truncateTo === ChronoUnitLegacy.MINUTES) return date;

  date.setMinutes(0);
  if (truncateTo === ChronoUnitLegacy.HOURS) return date;

  date.setHours(0);
  if (truncateTo === ChronoUnitLegacy.DAYS) return date;

  if (truncateTo === ChronoUnitLegacy.WEEKS) {
    return getMonday(date, zoneId);
  }

  date = getBeginningOfMonth(date, zoneId);
  if (truncateTo === ChronoUnitLegacy.MONTHS) return date;

  date = getBeginningOfYear(date, zoneId);
  if (truncateTo === ChronoUnitLegacy.YEARS) return date;

  return date;
}

export function addToDate(
  date:  Date | Instant | ZonedDateTime | string,
  amount: number,
  unit: ChronoUnitLegacy | String | undefined
): Date {
  date = toDate(date);

  if (amount == null || unit == null) {
    return date;
  }

  switch (unit) {
    case ChronoUnitLegacy.SECONDS:
      date.setSeconds(date.getSeconds() + amount);
      break;
    case ChronoUnitLegacy.MINUTES:
      date.setMinutes(date.getMinutes() + amount);
      break;
    case ChronoUnitLegacy.HALFHOUR:
      date.setMinutes(date.getMinutes() + amount * 30);
      break;
    case ChronoUnitLegacy.HOURS:
      date.setHours(date.getHours() + amount);
      break;
    case ChronoUnitLegacy.DAYS:
      date.setHours(date.getHours() + amount * 24);
      break;
    case ChronoUnitLegacy.WEEKS:
      date.setHours(date.getHours() + amount * 24 * 7);
      break;
    case ChronoUnitLegacy.MONTHS:
      date.setMonth(date.getMonth() + amount);
      break;
    case ChronoUnitLegacy.YEARS:
      date.setFullYear(date.getFullYear() + amount);
      break;
    default:
      break;
  }
  return date;
}


export function forceToLtTz<T>(date: T): T {
  if (date == null) return null as T;
  return convert(nativeJs(date).withZoneSameLocal(ZoneId.of("Europe/Vilnius"))).toDate() as T;
}

export function primereactDataTableSortDateFunction(e: ColumnSortEvent) {
  if (e.data) {
    const orderMultiplier = e.order || 1;
    return [...e.data].sort((a, b) => {
      const valA = a.startsAt || a.start ? new Date(a.startsAt || a.start).getTime() : NaN;
      const valB = b.startsAt || b.start ? new Date(b.startsAt || b.start).getTime() : NaN;

      if (Number.isNaN(valA) && !Number.isNaN(valB)) {
        return -1 * orderMultiplier;
      }
      if (!Number.isNaN(valA) && Number.isNaN(valB)) {
        return orderMultiplier;
      }
      return (valA - valB) * orderMultiplier;
    });
  }

  return []
}

function getUnitFromSeconds(seconds: number) {
  if (seconds < 60) {
    return ChronoUnit.SECONDS;
  }
  if (seconds < 3600) {
    return ChronoUnit.MINUTES;
  }
  if (seconds < 86400) {
    return ChronoUnit.HOURS;
  }
  return ChronoUnit.DAYS;
}

function getUnitLangKey(unit: ChronoUnit) {
  switch (unit) {
    case ChronoUnit.SECONDS:
      return "time.seconds";
    case ChronoUnit.MINUTES:
      return "time.minutes";
    case ChronoUnit.HOURS:
      return "time.hours";
    case ChronoUnit.DAYS:
      return "time.days";
    default:
      return "time.seconds";
  }
}

export function timeAgo(date: Date | string | undefined | null) {
  if (!date) return "";

  const secondsAgo = timeDifference(date);
  const unit = getUnitFromSeconds(secondsAgo);
  const unitLangKey = getUnitLangKey(unit);

  let count;
  switch (unit) {
    case ChronoUnit.SECONDS:
      count = secondsAgo;
      break;
    case ChronoUnit.MINUTES:
      count = Math.round(secondsAgo / 60);
      break;
    case ChronoUnit.HOURS:
      count = Math.round(secondsAgo / 3600);
      break;
    case ChronoUnit.DAYS:
      count = Math.round(secondsAgo / 86400);
      break;
    default:
      count = secondsAgo;
  }

  return T("message.time-ago", { count, unit: T(unitLangKey) });
}

export const timeDifference = (from: Date | string, to?: Date | string) => {
  const dateTo = to ? Instant.ofEpochMilli(new Date(from).getTime()) : Instant.now();
  const dateFrom = Instant.ofEpochMilli(new Date(from).getTime());
  const duration = Duration.between(dateFrom, dateTo).seconds();
  return Math.abs(duration);
};

export const dateBody = (date: string, dateFormat: ChronoUnitLegacy): string => {
  const dateDate = new Date(date);

  return dateToReadableString(dateDate, dateFormat);
};

export namespace DateUtils {
  export const strFormatDateTime = (
    date: string,
    pattern: string,
    zoneId: string = "Europe/Vilnius"
  ) => {
    const ins = Instant.parse(date);
    return LocalDateTime.ofInstant(ins, ZoneId.of(zoneId)).format(
      DateTimeFormatter.ofPattern(pattern)
    );
  };
}

export const secondsToTimeSpan = (seconds: number) => {
  const timeSpan = {
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  };
  if (!seconds || typeof seconds !== "number" || seconds <= 0) return timeSpan;

  timeSpan.seconds = seconds;
  if (seconds < 60) return timeSpan;

  timeSpan.minutes = Math.floor(seconds / 60);
  timeSpan.seconds %= 60;
  if (timeSpan.minutes < 60) return timeSpan;

  timeSpan.hours = Math.floor(timeSpan.minutes / 60);
  timeSpan.minutes %= 60;
  if (timeSpan.hours < 24) return timeSpan;

  timeSpan.days = Math.floor(timeSpan.hours / 24);
  timeSpan.hours %= 24;
  return timeSpan;
};

export type TimeSpan = {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
};

export const timeSpanToSeconds = (timeSpan: TimeSpan) => {
  if (!timeSpan || !(timeSpan instanceof Object) || Object.keys(timeSpan).length !== 4) return 0;

  const days = Math.abs(timeSpan.days) * 86400;
  const hours = Math.abs(timeSpan.hours) * 3600;
  const minutes = Math.abs(timeSpan.minutes) * 60;
  const seconds = Math.abs(timeSpan.seconds);
  return days + hours + minutes + seconds;
};

export const convertTimeSpanToString = (timeSpan: TimeSpan) => {
  if (!timeSpan) return "-";
  let timeString = "";

  if (timeSpan.days) {
    timeString += `${timeSpan.days}d `;
  }
  if (timeSpan.hours) {
    timeString += `${timeSpan.hours}h `;
  }
  if (timeSpan.minutes) {
    timeString += `${timeSpan.minutes}min `;
  }
  if (timeSpan.seconds) {
    timeString += `${timeSpan.seconds}s `;
  }
  return timeString.trim() || "-";
};

export const convertStringToTimeSpan = (timeSpanString: string) => {
  const timeSpan = {
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  };

  if (!timeSpanString || timeSpanString === "-") return timeSpan;

  const timeSpans = timeSpanString.split(" ");

  timeSpans.forEach(ts => {
    if (ts.includes("d")) {
      timeSpan.days = parseInt(ts.replace("d", ""), 10);
    } else if (ts.includes("h")) {
      timeSpan.hours = parseInt(ts.replace("h", ""), 10);
    } else if (ts.includes("min")) {
      timeSpan.minutes = parseInt(ts.replace("min", ""), 10);
    } else if (ts.includes("s")) {
      timeSpan.seconds = parseInt(ts.replace("s", ""), 10);
    }
  });

  return timeSpan;
};

export const formatToIsoStringWithoutMillis = (date: Date): string => {
  return `${date.toISOString().split(".")[0]}Z`;
};

export const formatDate = (
    date: string | undefined,
    formatPattern = DATE_PATTERN_MINUTES
) => {
  if (!date) return "";
  return formatDisplayDatetime(date, ZoneId.SYSTEM, formatPattern)
};

export const timeDuration = (start: string | undefined, end: string | undefined) => {
  if (!start || !end) return 0;
  return Duration.between(toInstant(start), toInstant(end)).seconds();
};

export const formatDateDuration = (start: string | undefined, end: string | undefined) => {
  if (!start || !end) return "-";
  let result;
  try {
    const offset = timeDuration(start, end);
    result = convertTimeSpanToString(secondsToTimeSpan(offset));
  } catch (error) {
    if (error instanceof Error) {
      result = error.message;
    }
    errorToaster(`Failed to parse "${start}" or "${end}" into date`);
  }
  return result;
};

export const getTimestamp = (val: unknown): number | null => {
  if (typeof val === "string" || typeof val === "number") {
    const date = new Date(val);
    return Number.isNaN(date.getTime()) ? null : date.getTime();
  }
  return null;
};

export const getRelativeOffsetForGroup = (
  groupStart: string,
  startTimeStr?: string,
  endTimeStr?: string
): [number, number] => {
  if (!startTimeStr || !groupStart) return [0, 0];
  const groupStartTime = new Date(groupStart).getTime();
  const start = new Date(startTimeStr).getTime();
  const end = endTimeStr ? new Date(endTimeStr).getTime() : start;
  return [start - groupStartTime, end - groupStartTime];
};


export const toZonedDateTime = (
  value: ZonedDateTime | Date | Instant | string,
  zoneId: ZoneId,
) => {
  return toInstant(value).atZone(zoneId);
};

export const toDate = (date: Instant | ZonedDateTime | Date | string | number): Date => {
  if (date instanceof Date) return date;

  if (date instanceof Instant) {
    return convert(date).toDate();
  }

  if (date instanceof ZonedDateTime) {
    return convert(date).toDate();
  }

  if(typeof date === "number") {
    return new Date(date);
  }

  try {
    return new Date(Date.parse(date));
  } catch {
    throw new Error(`${T("error.invalid-instant-type")} ${date} (${typeof date})`);
  }
}